import Cookies from 'js-cookie';
import contentUrlMapper from 'modules/experiences/utils/contentUrlMapper';
import Prismic from 'prismic-javascript';
import type ApiSearchResponse from 'prismic-javascript/d.ts/ApiSearchResponse';
import type { QueryOptions } from 'prismic-javascript/d.ts/ResolvedApi';
import type { Document } from 'prismic-javascript/src/documents';
import {
  CLASSICS_DOCUMENT_TYPE,
  COLLECTION_DOCUMENT_TYPE,
  HOST_TOOLS_DOCUMENT_TYPE,
  INFORMATION_PAGE_DOCUMENT_TYPE,
  LEARNING_CENTER_DOCUMENT_TYPE,
  LIVE_DOCUMENT_TYPE,
  LOCATION_PAGE_DOCUMENT_TYPE,
  LOCATION_PAGE_INTERLINKING_DOCUMENT_TYPE,
  PARTNER_CENTER_DOCUMENT_TYPE,
  PRISMIC_API_ENDPOINT,
  PRISMIC_API_ENDPOINT_V2,
  PRISMIC_API_ENDPOINT_V2_TOKEN,
  WL_CONNECT_DOCUMENT_TYPE,
} from 'utils/Prismic/PrismicConstants';
import PrismicResponseParser, {
  Article,
  ArticleSlice,
  Banner,
  BannerSlice,
  InterlinkingBlockProps,
} from 'utils/Prismic/PrismicResponseParser';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import { useI18n } from '~/stores/i18n';

export const prismicUidToPath = (uid: string) =>
  uid.replace(new RegExp('_', 'g'), '/');

const pathPrefixes = ['/experiences/', '/locations/', '/locals/', '/info/'];
export const pathToPrismicUid = (
  pathToCheck: string,
  splitOn: string[] = pathPrefixes,
): string => {
  const path = contentUrlMapper.map(pathToCheck);
  let result = '';
  splitOn.forEach((prefix) => {
    if (!path.includes(prefix) || result.length > 0) {
      return;
    }
    result = path
      .split(prefix)[1]
      .split('/')
      .filter((part) => part !== '')
      .join('_');
  });
  return result;
};

export const linkResolver = (doc: Pick<Document, 'type' | 'uid' | 'id'>) => {
  // Pretty URLs for known types
  if (doc.type === LOCATION_PAGE_DOCUMENT_TYPE) {
    return generatePathnameWithLangCode(
      `/prismic/${prismicUidToPath(doc.uid ?? '')}/`,
      useI18n.getState().lang,
    );
  }
  if (doc.type === INFORMATION_PAGE_DOCUMENT_TYPE) {
    return generatePathnameWithLangCode(
      `/info/${prismicUidToPath(doc.uid ?? '')}/`,
      useI18n.getState().lang,
    );
  }
  if (
    doc.type === LEARNING_CENTER_DOCUMENT_TYPE ||
    doc.type === PARTNER_CENTER_DOCUMENT_TYPE ||
    doc.type === CLASSICS_DOCUMENT_TYPE ||
    doc.type === LIVE_DOCUMENT_TYPE ||
    doc.type === WL_CONNECT_DOCUMENT_TYPE ||
    doc.type === HOST_TOOLS_DOCUMENT_TYPE
  ) {
    return generatePathnameWithLangCode(
      `/learning-center/preview-article/${doc.type}/${prismicUidToPath(
        doc.uid ?? '',
      )}/`,
      useI18n.getState().lang,
    );
  }
  if (doc.type === COLLECTION_DOCUMENT_TYPE) {
    return generatePathnameWithLangCode(
      `/learning-center/preview-collection/${prismicUidToPath(doc.uid ?? '')}/`,
      useI18n.getState().lang,
    );
  }

  // Fallback for other types, in case new custom types get created
  return '/doc/' + doc.id;
};

class PrismicClient {
  static query = (query: string | string[]): Promise<Document | undefined> => {
    return Prismic.getApi(PRISMIC_API_ENDPOINT, {
      accessToken: PRISMIC_API_TOKEN,
    })
      .then((api) => api.query(query, {} as QueryOptions))
      .then((resp) => resp.results[0]);
  };

  static getInformationPage = (
    uid: string,
    lang: string,
  ): Promise<Document | null> => {
    const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((client) => {
        const token = Cookies.get('Prismic-token');
        const opts: QueryOptions = token
          ? { lang: langParam, ref: token }
          : { lang: langParam };
        return client.getByUID(INFORMATION_PAGE_DOCUMENT_TYPE, uid, opts);
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getInformationPage(uid, 'en');
        }
        return response;
      })
      .catch((e) => {
        console.error(`Error in getInformationPage for ${uid} and ${lang}`, e);
        return null;
      });
  };

  static getCollection = (
    uid: string,
    lang: string,
  ): Promise<Document | null> => {
    const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((client) => {
        const token = Cookies.get('Prismic-token');
        const opts: QueryOptions = token
          ? { lang: langParam, ref: token }
          : { lang: langParam };
        return client.getByUID(COLLECTION_DOCUMENT_TYPE, uid, opts);
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getCollection(uid, 'en');
        }
        return response;
      })
      .catch((e) => {
        console.error(`Error in getInformationPage for ${uid} and ${lang}`, e);
        return null;
      });
  };

  static getLearningCenterArticles = (
    category: string,
    lang: string,
    docType: string,
  ): Promise<ApiSearchResponse | null> => {
    // const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((api) =>
        api.query(
          [
            Prismic.Predicates.at('document.type', docType),
            Prismic.Predicates.at(`my.${docType}.category`, category),
          ],
          { orderings: `[my.${docType}.order, my.${docType}.step]` },
        ),
      )
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getLearningCenterArticles(
            category,
            'en',
            docType,
          );
        }
        return response;
      })
      .catch((e) => {
        console.error(
          `Error in getLearningCenterArticles for ${category} and ${lang}`,
          e,
        );
        return null;
      });
  };

  static getTravelSafelyArticles = (
    category: string,
    lang: string,
    docType: string,
  ): Promise<ApiSearchResponse | null> => {
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((api) =>
        api.query(
          [
            Prismic.Predicates.at('document.type', docType),
            Prismic.Predicates.at(`my.${docType}.category`, category),
          ],
          { orderings: `[my.${docType}.order, my.${docType}.step]` },
        ),
      )
      .catch((e) => {
        console.error(
          `Error in ge getTravelSafelyArticles ${category} and ${lang}`,
          e,
        );
        return null;
      });
  };

  static getArticleByUid = (
    uid: string,
    lang: string,
    docType: string,
  ): Promise<Document | null> => {
    const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((client) => {
        const token = Cookies.get('Prismic-token');
        const opts: QueryOptions = token
          ? { lang: langParam, ref: token }
          : { lang: langParam };
        return client.getByUID(docType, uid, opts);
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getArticleByUid(uid, 'en', docType);
        }
        return response;
      })
      .catch((e) => {
        console.error(`Error in getInformationPage for ${uid} and ${lang}`, e);
        return null;
      });
  };

  static getLocationPageInterlinkDocument = (
    uid: string,
  ): Promise<Document | undefined> => {
    return PrismicClient.query([
      Prismic.Predicates.at(
        'document.type',
        LOCATION_PAGE_INTERLINKING_DOCUMENT_TYPE,
      ),
      Prismic.Predicates.at(
        `my.${LOCATION_PAGE_INTERLINKING_DOCUMENT_TYPE}.uid`,
        uid,
      ),
    ]);
  };

  static getLocationPageDocument = (
    uid: string,
  ): Promise<{
    documentId: string | undefined;
    topThingsTitle: string;
    topThingsDescription: string;
    topThingsToDo: Banner[];
    articles: Article[];
    interlinking: InterlinkingBlockProps[];
  }> => {
    type PrismicText = { text: string };
    type LocationTopItem = {
      slice_type: string;
      value: unknown[];
    };
    type LocationPageData = {
      topitems: LocationTopItem[];
      title?: PrismicText[];
      subtitle?: PrismicText[];
    };

    return PrismicClient.query([
      Prismic.Predicates.at('document.type', LOCATION_PAGE_DOCUMENT_TYPE),
      Prismic.Predicates.at(`my.${LOCATION_PAGE_DOCUMENT_TYPE}.uid`, uid),
    ]).then((results) => {
      if (!results || results.uid !== uid) {
        return {
          documentId: undefined,
          topThingsTitle: '',
          topThingsDescription: '',
          topThingsToDo: [],
          articles: [],
          interlinking: [],
        };
      }
      const documentId = results.id;
      const data = results.data as unknown as LocationPageData;
      const topThingsToDoProm = Promise.resolve(
        data.topitems
          .filter(
            (s): s is { slice_type: 'banner'; value: BannerSlice['value'] } =>
              s.slice_type === 'banner',
          )
          .map((slice) =>
            PrismicResponseParser.parseBannerSlice({
              slice_type: 'banner',
              slice_label: '',
              value: slice.value as BannerSlice['value'],
            }),
          ),
      );
      const articlesProm = Promise.resolve(
        data.topitems
          .filter(
            (s): s is { slice_type: 'article'; value: ArticleSlice['value'] } =>
              s.slice_type === 'article',
          )
          .map((slice) =>
            PrismicResponseParser.parseArticleSlice({
              slice_type: 'article',
              slice_label: '',
              value: slice.value as ArticleSlice['value'],
            }),
          ),
      );
      const topThingsTitle = data?.title?.[0]?.text || '';
      const topThingsDescription = data?.subtitle?.[0]?.text || '';
      const interlinkingProm = data.topitems
        .filter((topItem) => topItem.slice_type === 'interlink')
        .map((interlink) => {
          const first = interlink.value[0] as
            | Record<string, unknown>
            | undefined;
          const keys = first ? Object.keys(first) : [];
          const promises = keys
            .map((tab) => {
              const link = (first as Record<string, unknown>)[tab] as
                | { link_type?: string; uid?: string }
                | undefined;
              if (!link || link.link_type !== 'Document' || !link.uid) {
                return null;
              }
              return PrismicClient.getLocationPageInterlinkDocument(link.uid);
            })
            .filter(
              (val): val is Promise<Document | undefined> => val !== null,
            );
          return Promise.all(promises).then((interlinkingDocuments) =>
            interlinkingDocuments
              .filter((d): d is Document => !!d)
              .map((doc) =>
                PrismicResponseParser.parseInterlinkingDocument(doc),
              ),
          );
        })[0];

      return Promise.all([
        topThingsToDoProm,
        articlesProm,
        interlinkingProm,
      ]).then(([topThingsToDo, articles, interlinking]) => {
        return {
          documentId,
          topThingsTitle,
          topThingsDescription,
          topThingsToDo,
          articles,
          interlinking,
        };
      });
    });
  };

  static initPreview = (params: URLSearchParams) => {
    const token = params.get('token');
    if (token && token.indexOf('withlocalscontent')) {
      Cookies.set('Prismic-token', token);
      return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
        accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
      }).then((api) => api.previewSession(token ?? '', linkResolver, '/'));
    }
    return Prismic.getApi(PRISMIC_API_ENDPOINT, {
      accessToken: PRISMIC_API_TOKEN,
    }).then((api) => api.previewSession(token ?? '', linkResolver, '/'));
  };
}

export default PrismicClient;
